/** Instant search dropdown panel — category links left, product grid right. */

import { h } from 'preact';
import { useRef, useCallback } from 'preact/hooks';
import type { SearchResponse, AutocompleteResult, CategorySuggestion } from '../api';
import { InstantResultCard, InstantSkeletonCard } from './InstantResultCard';
import { useConditionalTitle } from '../hooks/useConditionalTitle';
import { QuickTooltip, useQuickTooltip } from './QuickTooltip';

interface InstantSearchPanelProps {
  suggestions: AutocompleteResult[];
  categories: CategorySuggestion[];
  popular: string[];
  results: SearchResponse | null;
  isSearchLoading: boolean;
  isLoadingMore: boolean;
  isAutocompleteLoading: boolean;
  query: string;
  onSelect: (name: string) => void;
  onViewAll: () => void;
  onLoadMore: () => void;
  onProductClick?: () => void;
}

function highlightMatch(text: string, query: string): h.JSX.Element {
  if (!query) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </span>
  );
}

function CategoryLink({ category, query }: { category: CategorySuggestion; query: string }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const tooltipText = useConditionalTitle(textRef, category.name);
  const { tooltip, showTooltip, moveTooltip, hideTooltip } = useQuickTooltip(tooltipText);

  return (
    <a
      href={category.category_url}
      class="ts-autocomplete-item ts-autocomplete-item--category"
      aria-label={category.name}
      onMouseEnter={(e) => showTooltip(e)}
      onMouseMove={(e) => moveTooltip(e)}
      onMouseLeave={hideTooltip}
      onFocus={(e) => showTooltip(e)}
      onBlur={hideTooltip}
    >
      <svg class="ts-autocomplete-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
      </svg>
      <div class="ts-autocomplete-item-content">
        <span ref={textRef} class="ts-autocomplete-item-text">
          {highlightMatch(category.name, query)}
        </span>
        <span class="ts-autocomplete-item-sub">{category.count} product{category.count === 1 ? '' : 's'}</span>
      </div>
      <svg class="ts-autocomplete-item-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <polyline points="9,18 15,12 9,6" />
      </svg>
      <QuickTooltip tooltip={tooltip} />
    </a>
  );
}

export function InstantSearchPanel({
  suggestions,
  categories,
  popular,
  results,
  isSearchLoading,
  isLoadingMore,
  isAutocompleteLoading,
  query,
  onSelect,
  onViewAll,
  onLoadMore,
}: InstantSearchPanelProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleGridScroll = useCallback(() => {
    const el = gridRef.current;
    if (!el || isLoadingMore) return;
    // Load more when within 200px of bottom
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      onLoadMore();
    }
  }, [onLoadMore, isLoadingMore]);

  const allProducts = results?.results ?? [];
  const total = results?.total ?? 0;

  // Build a category-name → real landing-page URL map from the products
  // already in the result set.  The /autocomplete payload populates
  // category_url for matched categories; facet-derived ones rely on this
  // fallback so they still link to the actual category page (e.g.
  // https://www.terrauniversal.com/cleanroom-equipment.php) instead of a
  // synthesised search query.
  const productCategoryUrlMap = new Map<string, string>();
  for (const p of allProducts) {
    if (p.category && p.category_url && !productCategoryUrlMap.has(p.category.toLowerCase())) {
      productCategoryUrlMap.set(p.category.toLowerCase(), p.category_url);
    }
  }
  const resolveCategoryUrl = (c: CategorySuggestion): string =>
    c.category_url || productCategoryUrlMap.get(c.name.toLowerCase()) || '';

  // Category source: merge autocomplete suggestions + search facets, dedupe by name, cap at 12.
  // Drop categories with no resolvable landing-page URL — surfacing them as
  // search-query links would mislead users into thinking they're navigating
  // to a category page.
  const facetCategories = (results?.facets?.category?.buckets ?? [])
    .filter((b) => b.value && b.count > 0)
    .map((b) => ({ name: b.value, count: b.count }));
  const seen = new Set<string>();
  const categoryLinks: CategorySuggestion[] = [...categories, ...facetCategories]
    .map((c) => ({ ...c, category_url: resolveCategoryUrl(c) }))
    .filter((c) => {
      if (!c.category_url) return false;
      const k = c.name.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, 12);

  // A settled zero-hit search must not leave an empty dropdown shell behind:
  // with no products, no category links and no request in flight there is
  // nothing to present, so the panel disappears entirely (the Autocomplete
  // fallback has the same guard).
  if (!isSearchLoading && allProducts.length === 0 && categoryLinks.length === 0) {
    return null;
  }

  return (
    <div class={`ts-instant-panel ${categoryLinks.length === 0 ? 'ts-instant-panel--no-left' : ''}`} role="dialog" aria-label="Search results preview">
      {/* Left column — category links (replaces the old suggestions/part-numbers/trending mix) */}
      {categoryLinks.length > 0 && (
        <div class="ts-instant-suggestions">
          {isAutocompleteLoading && categoryLinks.length === 0 && (
            <div class="ts-autocomplete-loading">Searching&hellip;</div>
          )}
          <div class="ts-autocomplete-section">
            <div class="ts-autocomplete-section-title">Shop by Category</div>
            {categoryLinks.map((c) => (
              <CategoryLink key={`cat-${c.name}`} category={c} query={query} />
            ))}
          </div>
        </div>
      )}

      {/* Right column — product grid split by vendor */}
      <div class="ts-instant-results-area">
        <div class="ts-instant-grid" ref={gridRef} onScroll={handleGridScroll}>
          {isSearchLoading && allProducts.length === 0
            ? Array.from({ length: 8 }).map((_, i) => <InstantSkeletonCard key={i} />)
            : allProducts.map((product, i) => (
                <InstantResultCard key={product.product_id} product={product} query={query} position={i} />
              ))}
          {isLoadingMore && (
            Array.from({ length: 4 }).map((_, i) => <InstantSkeletonCard key={`more-${i}`} />)
          )}
        </div>

        {/* Footer link into the full results page. Subtle by design \u2014 the
            prominent "more products" CTA lives on the results page itself
            (below the cards), where browse mode actually wants it. */}
        {(total > 0 || isSearchLoading) && (
          <button class="ts-instant-footer" onClick={onViewAll}>
            {isSearchLoading
              ? 'Searching\u2026'
              : `View all ${total.toLocaleString()} results \u2192`}
          </button>
        )}
      </div>
    </div>
  );
}
